import usePanel from '@databases/panel/usePanel';
import { isEmbed } from '@databases/utils/utils';
import React, { ReactNode, useEffect } from 'react';

const ResizeObserver: React.FC<{
  children?: ReactNode | undefined;
}> = ({ children }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const observedElement = ref.current;
  const { isPanelVisible } = usePanel();

  useEffect(() => {
    if (!observedElement || !isEmbed()) {
      return;
    }

    const minHeight = isPanelVisible() ? 560 : 400;
    const maxHeight = 600;
    let lastHeight: number | null = minHeight;
    const observer = new window.ResizeObserver(() => {
      const toolbarElement = document.getElementById('layout-toolbar-wrapper');

      let newHeight =
        observedElement.offsetHeight +
        // If there is no toolbar then we are rendering entry view.
        // 30px is added to compensate for page header margin + 6px buffer
        (toolbarElement?.getBoundingClientRect().height ?? 30) +
        // additional space under the content
        30;

      if (lastHeight === newHeight) {
        return;
      }

      if (newHeight < minHeight) {
        newHeight = minHeight;
      }

      if (newHeight > maxHeight) {
        newHeight = maxHeight;
      }

      lastHeight = newHeight;

      setTimeout(() => {
        window.parent.postMessage(
          JSON.stringify({ method: 'resize', height: Math.max(newHeight, 1) }),
          '*'
        );
      }, 0);
    });

    const registerResizeObserver = async () => {
      observer.observe(observedElement);
    };

    registerResizeObserver();

    return () => {
      observer.unobserve(observedElement);
    };
  }, [observedElement, isPanelVisible]);

  return (
    <div ref={ref} id={'resizeObserver'} style={{ display: 'block' }}>
      {children}
    </div>
  );
};

export default ResizeObserver;
